import React from 'react';
import {Formik, FormikHelpers, FormikProps, useFormikContext} from 'formik';

interface AppFormProps
  extends Omit<React.HTMLProps<HTMLDivElement>, 'onSubmit'> {
  initialValues: any;
  onSubmit(values: any, formikHelpers: FormikHelpers<any>): void | Promise<any>;
  validationSchema: any;
  children?: React.ReactNode;
  onChange?(values: any): void;
  refundAutoScroll?: boolean;
  preRefunded?: boolean;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
}

/* ---------- helpers de scroll ---------- */
const scrollEl = (el: Element | null) => {
  if (!el) return;

  // Scroll the element into view with better positioning
  (el as HTMLElement).scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  });

  // Focus the element if it's focusable
  const htmlEl = el as HTMLElement;
  if (
    htmlEl.focus &&
    (htmlEl.tagName === 'INPUT' ||
      htmlEl.tagName === 'SELECT' ||
      htmlEl.tagName === 'TEXTAREA')
  ) {
    // Small delay to ensure scroll completes before focusing
    setTimeout(() => {
      htmlEl.focus();
    }, 300);
  }
};

const firstErrorPath = (
  err: any,
  prefix = '',
  preRefunded = false
): string | null => {
  if (!err || typeof err !== 'object') return null;

  // Define visual order of fields based on form layout
  const fieldOrder = preRefunded
    ? [
        'noDocuments', // File upload appears first in preRefund mode
        'patient',
        'dateOfOcurrence',
        'expenses',
        'termsAccepted',
      ]
    : [
        'patient',
        'dateOfOcurrence',
        'expenses',
        'noDocuments', // File upload appears after expenses in normal mode
        'termsAccepted',
      ];

  // Sort keys based on visual order, putting unknown fields at the end
  const keys = Object.keys(err).sort((a, b) => {
    const aIndex = fieldOrder.indexOf(a);
    const bIndex = fieldOrder.indexOf(b);

    // If both fields are in our order list, sort by their position
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only one field is in our list, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // If neither field is in our list, keep original order
    return 0;
  });

  for (const key of keys) {
    const val = err[key];

    if (val && typeof val === 'string') {
      // Direct error message - this is our target
      return prefix ? `${prefix}.${key}` : key;
    } else if (Array.isArray(val)) {
      // Find first array element with errors
      const idx = val.findIndex(
        (e) => e && typeof e === 'object' && Object.keys(e).length > 0
      );
      if (idx >= 0) {
        const arrayPath = `${prefix ? `${prefix}.` : ''}${key}[${idx}]`;
        // Recurse into the array element
        const nested = firstErrorPath(val[idx], arrayPath);
        return nested || arrayPath;
      }
    } else if (val && typeof val === 'object') {
      // Nested object
      const nested = firstErrorPath(val, prefix ? `${prefix}.${key}` : key);
      if (nested) return nested;
    }
  }
  return null;
};

const getElementForPath = (path: string): Element | null => {
  // Try direct matches first
  let el =
    document.querySelector(`[name="${path}"]`) ||
    document.querySelector(`[id="${path}"]`);
  if (el) return el;

  // Handle array paths like expenses[0].coverage
  const arrayMatch = path.match(/^(.+?)\[(\d+)\]\.(.+)$/);
  if (arrayMatch) {
    const [, , index, field] = arrayMatch;

    // Try multiple selector strategies for expense fields
    const selectors = [
      `[data-expense-index="${index}"] [name="${path}"]`,
      `[data-expense-index="${index}"] [name="${field}"]`,
      `[data-expense-index="${index}"] input`,
      `[data-expense-index="${index}"] select`,
      `[data-expense-index="${index}"]`,
      // Alternative: look for form fields within expense containers
      `.reimbursement-data-inputs[data-expense-index="${index}"] input`,
      `.reimbursement-data-inputs[data-expense-index="${index}"] select`,
    ];

    for (const selector of selectors) {
      el = document.querySelector(selector);
      if (el) return el;
    }
  }

  // Handle specific field types
  if (path === 'dateOfOcurrence' || path.includes('date')) {
    el =
      document.querySelector('.date-picker-row input') ||
      document.querySelector('[name="dateOfOcurrence"]') ||
      document.querySelector('.date-picker-row');
    if (el) return el;
  }

  if (path === 'patient') {
    el =
      document.querySelector('[name="patient"]') ||
      document.querySelector('.reimbursement-data-firstrow select');
    if (el) return el;
  }

  if (path === 'termsAccepted') {
    el =
      document.querySelector('[name="termsAccepted"]') ||
      document.querySelector('input[type="checkbox"]');
    if (el) return el;
  }

  if (path === 'noDocuments') {
    el =
      document.querySelector('[name="noDocuments"]') ||
      document.querySelector('.app-drag-drop-container') ||
      document.querySelector('.attach-files-container');
    if (el) return el;
  }

  // Handle expenses array errors by scrolling to the expenses section
  if (path === 'expenses' || path.startsWith('expenses[')) {
    el =
      document.querySelector('.reimbursement-data-secondrow-gradient') ||
      document.querySelector('.reimbursement-data-inputs') ||
      document.querySelector('[data-expense-index="0"]');
    if (el) return el;
  }

  return null;
};

const ScrollToFirstError: React.FC<{preRefunded?: boolean}> = ({
  preRefunded,
}) => {
  const {submitCount, isValid, isValidating, errors} = useFormikContext<any>();
  const prevSubmitCountRef = React.useRef(0);

  React.useEffect(() => {
    // Only scroll if this is a new submit attempt (submitCount increased) and form is invalid
    if (submitCount <= prevSubmitCountRef.current || isValidating || isValid) {
      return;
    }

    prevSubmitCountRef.current = submitCount;

    // Wait for DOM updates and error rendering
    const scrollToError = () => {
      const path = firstErrorPath(errors, '', preRefunded);

      if (!path) return;

      const el = getElementForPath(path);

      if (el) {
        scrollEl(el);
      } else {
        // Fallback: try again after a longer delay if element not found
        setTimeout(() => {
          const retryEl = getElementForPath(path);
          if (retryEl) {
            scrollEl(retryEl);
          }
        }, 100);
      }
    };

    // Use multiple timing strategies to ensure DOM is ready
    requestAnimationFrame(() => {
      setTimeout(scrollToError, 50);
    });
  }, [submitCount, isValidating, isValid, errors, preRefunded]);

  return null;
};
/* ---------- fim helpers ---------- */

function AppForm({
  initialValues,
  onSubmit,
  validationSchema,
  children,
  onChange,
  refundAutoScroll,
  preRefunded,
  validateOnChange = true,
  validateOnBlur = true,
  ...rest
}: AppFormProps) {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={validateOnChange}
      validateOnBlur={validateOnBlur}>
      {(formik: FormikProps<any>) => {
        onChange?.(formik.values);
        return (
          <div {...rest}>
            {/* Scroll to first error when submit fails */}
            {refundAutoScroll && (
              <ScrollToFirstError preRefunded={preRefunded} />
            )}
            {children}
          </div>
        );
      }}
    </Formik>
  );
}

export default AppForm;
